import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { MoviesAPI } from '../../api';
import ContentCard from '../Cards/ContentCard';
import HoverCard from '../Cards/HoverCard';

const PageContainer = styled.div`
  padding: 100px 30px 20px; /* Added top padding for header */
  
  @media (max-width: 768px) {
    padding: 100px 20px 20px; /* Added top padding for header */
  }
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 20px;
`;

const FilterTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 20px;
`;

const FilterTag = styled.span`
  padding: 8px 15px;
  background-color: ${props => props.$active ? 'var(--primary-color)' : 'var(--background-light)'};
  border-radius: 20px;
  margin-right: 10px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: background-color var(--transition-speed);
  
  &:hover {
    background-color: ${props => props.$active ? 'var(--primary-color)' : 'rgba(255, 255, 255, 0.1)'};
  }
`;

const MoviesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 20px;
  min-height: 300px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  }
`;

const LoadMoreButton = styled.button`
  background-color: var(--background-light);
  color: var(--text-light);
  border: none;
  padding: 10px 20px;
  border-radius: var(--border-radius);
  margin: 30px auto 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color var(--transition-speed);
  
  &:hover {
    background-color: var(--primary-color);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    margin-right: 8px;
  }
`;

// new overlay added
const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
`;

const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  
  svg {
    font-size: 40px;
    color: var(--primary-light);
    animation: spin 1s linear infinite;
  }
  
  p {
    margin-top: 15px;
    color: white;
    font-size: 16px;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const GridLoadingIndicator = styled.div`
  grid-column: 1 / -1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 50px 0;
  
  svg {
    font-size: 30px;
    color: var(--primary-light);
    animation: spin 1s linear infinite;
  }
`;

const MoviesPage = () => {
  const [movies, setMovies] = useState([]);
  const [filteredMovies, setFilteredMovies] = useState([]);
  const [activeTag, setActiveTag] = useState('All');
  const [tags, setTags] = useState(['All']);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false); 
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  
  //unique tags from all movies
  const extractTags = (movies) => {
    const allTags = movies.flatMap(movie => movie.tags || []);
    return ['All', ...new Set(allTags)];
  };
  
  //initial movies
  useEffect(() => {
    const fetchMovies = async () => {
      try {
        setLoading(true);
        const response = await MoviesAPI.getAll({ page: 1, limit: 20 });
        const { data, pagination } = response.data;
        
        setMovies(data);
        setFilteredMovies(data);
        setTags(extractTags(data));
        setHasMore(pagination.page < pagination.pages);
      } catch (err) {
        console.error('Error fetching movies:', err);
        setError('Failed to load movies. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchMovies();
  }, []);
  
  const handleTagClick = (tag) => {
    if (tag === activeTag) return; // Dont refilter if the same tag is clicked
    
    setFilterLoading(true);
    setActiveTag(tag);
    
    setTimeout(() => {
      if (tag === 'All') {
        setFilteredMovies(movies);
      } else {
        const filtered = movies.filter(movie => 
          movie.tags && movie.tags.includes(tag)
        );
        setFilteredMovies(filtered);
      }
      setFilterLoading(false);
    }, 300);
  };
  
  //more movies
  const loadMoreMovies = async () => {
    try {
      setLoadMoreLoading(true);
      const nextPage = page + 1;
      const response = await MoviesAPI.getAll({ page: nextPage, limit: 20 });
      const { data, pagination } = response.data;
      
      const newMovies = [...movies, ...data];
      setMovies(newMovies);
      setPage(nextPage);
      setHasMore(pagination.page < pagination.pages);
      
      // Update tags
      setTags(extractTags(newMovies));
      
      // Update filtered movies if All is selected
      if (activeTag === 'All') {
        setFilteredMovies(newMovies);
      } else {
        const filtered = newMovies.filter(movie => 
          movie.tags && movie.tags.includes(activeTag)
        );
        setFilteredMovies(filtered);
      }
    } catch (err) {
      console.error('Error loading more movies:', err);
      setError('Failed to load more movies. Please try again later.');
    } finally {
      setLoadMoreLoading(false);
    }
  };
  
  if (loading && movies.length === 0) {
    return (
      <LoadingOverlay>
        <LoadingSpinner>
          <FontAwesomeIcon icon={faSpinner} />
          <p>Loading movies...</p>
        </LoadingSpinner>
      </LoadingOverlay>
    );
  }
  
  if (error && movies.length === 0) {
    return <div className="error-message">{error}</div>;
  }
  
  return (
    <PageContainer>
      <PageTitle>Movies</PageTitle>
      
      <FilterTags>
        {tags.map((tag, index) => (
          <FilterTag 
            key={index} 
            $active={activeTag === tag}
            onClick={() => handleTagClick(tag)}
          >
            {tag}
          </FilterTag>
        ))}
      </FilterTags>
      
      <MoviesGrid>
        {filterLoading ? (
          <GridLoadingIndicator>
            <FontAwesomeIcon icon={faSpinner} />
          </GridLoadingIndicator>
        ) : (
          filteredMovies.map(movie => (
            movie.hoverImage ? (
              <HoverCard key={movie._id} item={movie} type="movie" />
            ) : (
              <ContentCard key={movie._id} item={movie} type="movie" />
            )
          ))
        )}
        
        {filteredMovies.length === 0 && !filterLoading && (
          <div style={{ 
            gridColumn: '1 / -1', 
            padding: '30px', 
            textAlign: 'center',
            color: 'var(--text-dim)',
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '8px'
          }}>
            No movies found for this filter. Try another category.
          </div>
        )}
      </MoviesGrid>
      
      {hasMore && (
        <LoadMoreButton 
          onClick={loadMoreMovies} 
          disabled={loadMoreLoading}
        >
          {loadMoreLoading ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin /> Loading...
            </>
          ) : (
            'Load More'
          )}
        </LoadMoreButton>
      )}
    </PageContainer>
  );
};

export default MoviesPage;
